import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import AnalyticsService from '../../../services/AnalyticsService';
import DateRangeFilter from '../../StationOverview/components/DateRangeFilter';

type RangeKey = '1d' | '7d' | '30d' | '90d';

const rangeToDays: Record<RangeKey, number> = {
  '1d': 1,
  '7d': 7,
  '30d': 30,
  '90d': 90,
};

const formatEuros = (euros: number): string =>
  euros.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });

const formatMonth = (bucket: string): string => {
  const [year, month] = bucket.split('-').map(Number);
  // Include the year so months read unambiguously across a full year / multi-year range.
  return new Date(year, (month ?? 1) - 1, 1).toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' });
};

// Weekly buckets arrive as the Monday of the week, daily buckets as the day itself (both YYYY-MM-DD).
const formatDate = (bucket: string): string => {
  const [year, month, day] = bucket.split('-').map(Number);
  return new Date(year, (month ?? 1) - 1, day ?? 1).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

const pad2 = (n: number): string => String(n).padStart(2, '0');

// The API only returns buckets that contain paid invoices, so periods without
// revenue would silently disappear from the chart. Enumerate every bucket key
// of the selected range to fill the gaps with zeros.
const enumerateBuckets = (start: Date, end: Date, groupBy: 'month' | 'week' | 'day'): string[] => {
  const keys: string[] = [];
  if (groupBy === 'week' || groupBy === 'day') {
    const cursor = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    if (groupBy === 'week') {
      cursor.setDate(cursor.getDate() - ((cursor.getDay() + 6) % 7)); // back to Monday
    }
    const stepDays = groupBy === 'week' ? 7 : 1;
    while (cursor <= end) {
      keys.push(`${cursor.getFullYear()}-${pad2(cursor.getMonth() + 1)}-${pad2(cursor.getDate())}`);
      cursor.setDate(cursor.getDate() + stepDays);
    }
  } else {
    const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
    while (cursor <= end) {
      keys.push(`${cursor.getFullYear()}-${pad2(cursor.getMonth() + 1)}`);
      cursor.setMonth(cursor.getMonth() + 1);
    }
  }
  return keys;
};

const formatDuration = (minutes: number | null): string => {
  if (minutes == null) return '--';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h ${m}min` : `${m}min`;
};

const Card: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="p-4 bg-white rounded-lg shadow-card outline outline-1 outline-offset-[-1px] outline-neutral-10 flex flex-col gap-4">
    {children}
  </div>
);

interface Props {
  stationId: number;
}

type RevenueGroupBy = 'month' | 'week' | 'day';

// Daily buckets are only offered on short ranges (mirrors the API-side cap):
// beyond this the chart is unreadable and the response unbounded.
const MAX_DAY_RANGE_DAYS = 120;

const groupByTitles: Record<RevenueGroupBy, string> = {
  month: 'Revenu par mois',
  week: 'Revenu par semaine',
  day: 'Revenu par jour',
};

const StationAnalytics: React.FC<Props> = ({ stationId }) => {
  const [selectedRange, setSelectedRange] = useState<RangeKey>('90d');
  const [revenueGroupBy, setRevenueGroupBy] = useState<RevenueGroupBy>('month');
  // Default to the current year so the statistics open on a useful, year-scoped view.
  // The custom range picker then lets you select last year or any specific year.
  const [customRange, setCustomRange] = useState<{ start: Date; end: Date } | null>(() => ({
    start: new Date(new Date().getFullYear(), 0, 1),
    end: new Date(),
  }));

  const { startDate, endDate } = useMemo(() => {
    if (customRange) {
      return { startDate: customRange.start, endDate: customRange.end };
    }
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - rangeToDays[selectedRange]);
    return { startDate: start, endDate: end };
  }, [selectedRange, customRange]);

  const handleRangeChange = useCallback((range: RangeKey) => {
    setSelectedRange(range);
    setCustomRange(null);
  }, []);

  const handleCustomRangeApply = useCallback((start: Date, end: Date) => {
    setCustomRange({ start, end });
  }, []);

  const rangeKey = customRange
    ? `custom-${startDate.toISOString()}-${endDate.toISOString()}`
    : selectedRange;

  const rangeDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (24 * 60 * 60 * 1000));
  const isDayAvailable = rangeDays <= MAX_DAY_RANGE_DAYS;
  // If the range grows past the cap while "Jour" is selected, fall back to weeks
  // rather than firing a request the API would reject.
  const effectiveGroupBy: RevenueGroupBy = revenueGroupBy === 'day' && !isDayAvailable ? 'week' : revenueGroupBy;

  const { data: revenue, isLoading: isRevenueLoading } = useQuery({
    queryKey: ['station-revenue', stationId, rangeKey, effectiveGroupBy],
    queryFn: () => AnalyticsService.getRevenue(startDate, endDate, { groupBy: effectiveGroupBy, stationId }),
  });

  const { data: stats, isLoading: isStatsLoading } = useQuery({
    queryKey: ['station-rental-stats', stationId, rangeKey],
    queryFn: () => AnalyticsService.getRentalStats(startDate, endDate, { stationId }),
  });

  const revenueChartData = useMemo(() => {
    if (!revenue || revenue.buckets.length === 0) return [];
    const formatBucket = effectiveGroupBy === 'month' ? formatMonth : formatDate;
    const eurosByBucket = new Map(revenue.buckets.map((bucket) => [bucket.bucket, bucket.revenueEuros]));
    return enumerateBuckets(startDate, endDate, effectiveGroupBy).map((key) => ({
      label: formatBucket(key),
      euros: eurosByBucket.get(key) ?? 0,
    }));
  }, [revenue, effectiveGroupBy, startDate, endDate]);

  return (
    <div className="flex flex-col gap-4">
      {/* Range selector — same filter as the other views (presets + custom range) */}
      <DateRangeFilter
        selectedRange={selectedRange}
        onRangeChange={handleRangeChange}
        startDate={startDate}
        endDate={endDate}
        isCustomRange={customRange !== null}
        onCustomRangeApply={handleCustomRangeApply}
      />

      {/* Summary stats */}
      <div className="flex flex-wrap gap-3">
        <div className="flex-1 min-w-[140px] p-4 bg-white rounded-lg shadow-card outline outline-1 outline-offset-[-1px] outline-neutral-10 flex flex-col gap-1">
          <span className="text-neutral-60 typo-small">Revenu encaissé</span>
          <span className="text-neutral typo-h2">{isRevenueLoading ? '...' : formatEuros(revenue?.totalEuros ?? 0)}</span>
        </div>
        <div className="flex-1 min-w-[140px] p-4 bg-white rounded-lg shadow-card outline outline-1 outline-offset-[-1px] outline-neutral-10 flex flex-col gap-1">
          <span className="text-neutral-60 typo-small">Réservations</span>
          <span className="text-neutral typo-h2">{isStatsLoading ? '...' : stats?.totalRentals ?? 0}</span>
        </div>
        <div className="flex-1 min-w-[140px] p-4 bg-white rounded-lg shadow-card outline outline-1 outline-offset-[-1px] outline-neutral-10 flex flex-col gap-1">
          <span className="text-neutral-60 typo-small">Durée moyenne</span>
          <span className="text-neutral typo-h2">{isStatsLoading ? '...' : formatDuration(stats?.averageDurationMinutes ?? null)}</span>
        </div>
      </div>

      {/* Revenue over time (monthly, weekly or daily buckets) */}
      <Card>
        <div className="flex items-center justify-between gap-4">
          <h3 className="text-neutral typo-h3">{groupByTitles[effectiveGroupBy]}</h3>
          <div className="flex items-center gap-1">
            {([
              { key: 'month', label: 'Mois' },
              { key: 'week', label: 'Semaine' },
              { key: 'day', label: 'Jour' },
            ] as { key: RevenueGroupBy; label: string }[]).map(({ key, label }) => {
              const disabled = key === 'day' && !isDayAvailable;
              return (
                <button
                  key={key}
                  type="button"
                  disabled={disabled}
                  title={disabled ? `Disponible pour les périodes de ${MAX_DAY_RANGE_DAYS} jours maximum` : undefined}
                  className={`px-2.5 py-1 rounded-md typo-body transition-colors ${
                    effectiveGroupBy === key
                      ? 'bg-neutral text-white !font-semibold cursor-default'
                      : disabled
                        ? 'text-neutral-30 cursor-not-allowed'
                        : 'text-neutral-60 hover:text-neutral'
                  }`}
                  onClick={() => setRevenueGroupBy(key)}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
        <div className="h-[240px]">
          {isRevenueLoading ? (
            <div className="h-full flex items-center justify-center">
              <span className="text-neutral-40 typo-small">Chargement...</span>
            </div>
          ) : revenueChartData.length === 0 ? (
            <div className="h-full flex items-center justify-center">
              <span className="text-neutral-40 typo-small">Aucun revenu sur la période sélectionnée.</span>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={revenueChartData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                <CartesianGrid stroke="rgba(26,26,26,0.05)" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 13, fill: 'rgba(26,26,26,0.6)' }}
                  tickLine={false}
                  axisLine={false}
                  // Weekly/daily views can hold up to ~120 buckets — let Recharts skip overlapping labels.
                  interval="preserveStartEnd"
                  minTickGap={16}
                />
                <YAxis
                  tick={{ fontSize: 13, fill: 'rgba(26,26,26,0.6)' }}
                  tickLine={false}
                  axisLine={false}
                  width={50}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1A1A1A',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 12px',
                    fontSize: '13px',
                    color: 'white',
                  }}
                  formatter={(value: any) => [formatEuros(Number(value)), 'Revenu']}
                />
                <Bar dataKey="euros" fill="#FF8A4C" radius={4} maxBarSize={32} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </Card>

      {/* Rental breakdown */}
      <div className="flex flex-wrap gap-3">
        <Card>
          <h3 className="text-neutral typo-h3">Par statut</h3>
          {stats && stats.byStatus.length > 0 ? (
            <ul className="flex flex-col gap-1.5">
              {stats.byStatus.map((row) => (
                <li key={row.status} className="flex items-center justify-between gap-4">
                  <span className="text-neutral-60 typo-small">{row.status}</span>
                  <span className="text-neutral typo-body">{row.count}</span>
                </li>
              ))}
            </ul>
          ) : (
            <span className="text-neutral-40 typo-small">Aucune donnée.</span>
          )}
        </Card>
        <Card>
          <h3 className="text-neutral typo-h3">Par type d'équipement</h3>
          {stats && stats.byEquipmentType.length > 0 ? (
            <ul className="flex flex-col gap-1.5">
              {stats.byEquipmentType.map((row) => (
                <li key={row.type} className="flex items-center justify-between gap-4">
                  <span className="text-neutral-60 typo-small">{row.type}</span>
                  <span className="text-neutral typo-body">{row.count}</span>
                </li>
              ))}
            </ul>
          ) : (
            <span className="text-neutral-40 typo-small">Aucune donnée.</span>
          )}
        </Card>
      </div>
    </div>
  );
};

export default StationAnalytics;
